
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import logger from "@/utils/logger";
import clarity from "@microsoft/clarity";
import { sanitizeTrackingHtml } from "@/utils/sanitizeTrackingHtml";

interface TrackingCodeInjectorProps {
  googleVerification?: string;
}

export const TrackingCodeInjector = ({ 
  googleVerification = "usNrj9zFT_gF3wCSzaD5_YldcdV_4tVVCcsViwZ3F7U" 
}: TrackingCodeInjectorProps) => {
  const [headCode, setHeadCode] = useState<string>("");
  const [bodyCode, setBodyCode] = useState<string>("");
  const [loading, setLoading] = useState(true);
  // Récupérer le nonce depuis le document si disponible
  const nonce = document.querySelector('script[nonce]')?.getAttribute('nonce') || '';

  useEffect(() => {
    // Initialiser Microsoft Clarity avec l'ID de votre projet
    try {
      // Récupérer l'ID de projet Clarity depuis les variables d'environnement
      const clarityProjectId = import.meta.env.VITE_CLARITY_PROJECT_ID;
      
      if (clarityProjectId) {
        clarity.init(clarityProjectId);
        logger.log("Microsoft Clarity initialized successfully with project ID:", clarityProjectId);
      } else {
        logger.warn("Microsoft Clarity project ID is missing from environment variables");
      }
    } catch (error) {
      logger.error("Failed to initialize Microsoft Clarity:", error);
    }

    const fetchTrackingCodes = async () => {
      try {
        // Utiliser maybeSingle au lieu de single pour éviter l'erreur lorsqu'aucune ligne n'est trouvée
        const { data, error } = await supabase
          .from('tracking_codes')
          .select('*')
          .maybeSingle();

        if (error) {
          logger.error('Erreur lors du chargement des codes de tracking:', error);
          return;
        }

        if (data) {
          // Filtrer la balise meta de vérification Google si elle existe
          let cleanedHeadCode = data.head_code || '';
          cleanedHeadCode = cleanedHeadCode.replace(/<meta name="google-site-verification" content="[^"]*" \/>/g, '');
          
          // Filtrer également toute balise meta CSP si elle existe
          cleanedHeadCode = cleanedHeadCode.replace(/<meta name="Content-Security-Policy" content="[^"]*">/g, '');
          cleanedHeadCode = cleanedHeadCode.replace(/<meta http-equiv="Content-Security-Policy" content="[^"]*">/g, '');
          
          // Ajouter nonce pour les scripts pour Clarity
          cleanedHeadCode = cleanedHeadCode.replace(/<script(?![^>]*nonce=)/g, `<script nonce="${nonce}"`);
          
          setHeadCode(sanitizeTrackingHtml(cleanedHeadCode));
          setBodyCode(sanitizeTrackingHtml(data.body_code || ''));
        }
      } catch (error) {
        logger.error('Exception lors du chargement des codes de tracking:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTrackingCodes();
  }, []);

  if (loading || (!headCode && !bodyCode && !googleVerification)) {
    return null;
  }

  return (
    <Helmet>
      {googleVerification && (
        <meta name="google-site-verification" content={googleVerification} />
      )}
      
      {headCode && <script type="text/javascript" nonce={nonce} dangerouslySetInnerHTML={{ __html: `
        document.addEventListener('DOMContentLoaded', function() {
          const headContainer = document.createElement('div');
          headContainer.innerHTML = ${JSON.stringify(headCode)};
          
          // Ajuster les nonces pour les scripts dans headContent
          Array.from(headContainer.children).forEach(node => {
            if (node.tagName === 'SCRIPT') {
              node.setAttribute('nonce', '${nonce}');
            }
            document.head.appendChild(node);
          });
        });
      `}} />}
      
      {bodyCode && <script type="text/javascript" nonce={nonce} dangerouslySetInnerHTML={{ __html: `
        document.addEventListener('DOMContentLoaded', function() {
          const bodyContainer = document.createElement('div');
          bodyContainer.innerHTML = ${JSON.stringify(bodyCode)};
          
          // Ajuster les nonces pour les scripts dans bodyContent
          Array.from(bodyContainer.children).forEach(node => {
            if (node.tagName === 'SCRIPT') {
              node.setAttribute('nonce', '${nonce}');
            }
            document.body.appendChild(node);
          });
        });
      `}} />}
    </Helmet>
  );
};
